import { useTranslation } from "react-i18next";
import { Label } from "../../../../helpers/historyHelpers/components/Label";
import moment from "moment-timezone";
import { NewValue } from "../../../../helpers/historyHelpers/components/NewValue";
import { OldValue } from "../../../../helpers/historyHelpers/components/OldValue";
import { useCompanyCurrency } from "../../../../../../utils/hooks/company";
import { renderCurrencySymbol } from "../../../../../../utils/helpers/job";
import { firstToUpper } from "../../../../../../utils/helpers/string";
import { renderCompanyResourceLabel, renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";

const fields = ["company", "location", "type", "startOfWeek", "level", "payGroup", "months", "weekDays", "weeks"];

const budgetCases = (item, data, currency, t) => {
    const months = moment.months().map((month) => month.toLowerCase());
    const weeks = Array.from({ length: 53 }, (_, i) => `week_${i + 1}`);

    switch (item) {
        case "company":
            const newValueCompany = data[item]?.new;
            const oldValueCompany = data[item]?.old;

            return (
                <div className="d-flex align-items-center">
                    {newValueCompany && <OldValue value={renderCompanyResourceLabel(newValueCompany)} />}
                    {oldValueCompany && <NewValue value={renderCompanyResourceLabel(oldValueCompany)} />}
                </div>
            );
        case "type":
        case "startOfWeek":
            const newValueType = data[item]?.new;
            const oldValueType = data[item]?.old;

            return (
                <div className="d-flex align-items-center">
                    <OldValue value={firstToUpper(t(oldValueType))} />
                    <NewValue value={firstToUpper(t(newValueType))} />
                </div>
            );
        case "location":
        case "level":
        case "payGroup":
            const newValue = data[item]?.new;
            const oldValue = data[item]?.old;

            return (
                <div className="d-flex align-items-center">
                    {newValue && <OldValue value={renderSettingsResourceLabel(newValue)} />}
                    {oldValue && <NewValue value={renderSettingsResourceLabel(oldValue)} />}
                </div>
            );
        case "months":
            return months.map((month) => {
                const monthData = data[item]?.[month];
                const newAmount = monthData?.amount?.new;
                const oldAmount = monthData?.amount?.old;
                const newHours = monthData?.hours?.new;
                const oldHours = monthData?.hours?.old;

                const hasValues =
                    newAmount !== undefined ||
                    newHours !== undefined ||
                    oldAmount !== undefined ||
                    oldHours !== undefined;

                if (!hasValues) return null;

                const formattedNewValue = (
                    <div className="d-flex flex-column">
                        {newAmount !== undefined && (
                            <span>
                                {t("new-amount")}: {renderCurrencySymbol(currency)}
                                {newAmount}
                            </span>
                        )}
                        {newHours !== undefined && (
                            <span>
                                {t("new-hours")}: {newHours}
                            </span>
                        )}
                    </div>
                );

                const formattedOldValue = (
                    <div className="d-flex flex-column">
                        {oldAmount !== undefined && (
                            <span>
                                {t("old-amount")}: {renderCurrencySymbol(currency)}
                                {oldAmount}
                            </span>
                        )}
                        {oldHours !== undefined && (
                            <span>
                                {t("old-hours")}: {oldHours}
                            </span>
                        )}
                    </div>
                );

                return (
                    <div key={month} className="d-flex align-items-center gap-2">
                        {t(month)}:
                        <OldValue value={formattedOldValue} />
                        <NewValue value={formattedNewValue} />
                    </div>
                );
            });
        case "weeks":
            return weeks.map((week) => {
                const weekValue = data[item]?.[week];

                const newStartDate = weekValue?.startDate?.new;
                const oldStartDate = weekValue?.startDate?.old;
                const newEndDate = weekValue?.endDate?.new;
                const oldEndDate = weekValue?.endDate?.old;

                const newAmount = weekValue?.amount?.new;
                const oldAmount = weekValue?.amount?.old;
                const newHour = weekValue?.hours?.new;
                const oldHour = weekValue?.hours?.old;

                const hasAnyData =
                    newStartDate != null ||
                    oldStartDate != null ||
                    newEndDate != null ||
                    oldEndDate != null ||
                    newAmount != null ||
                    oldAmount != null ||
                    newHour != null ||
                    oldHour != null;

                if (!hasAnyData) return null;

                const weekNumber = parseInt(week.match(/\d+/)?.[0]);

                const formattedNewValue = (
                    <div className="d-flex flex-column">
                        {newStartDate && (
                            <span>
                                {t("start-date")}: {newStartDate}
                            </span>
                        )}
                        {newEndDate && (
                            <span>
                                {t("end-date")}: {newEndDate}
                            </span>
                        )}
                        {typeof newAmount === "number" && (
                            <span>
                                {t("amount")}: {newAmount}
                            </span>
                        )}
                        {typeof newHour === "number" && (
                            <span>
                                {firstToUpper(t("hour"))}: {newHour}
                            </span>
                        )}
                    </div>
                );

                const formattedOldValue = (
                    <div className="d-flex flex-column">
                        {oldStartDate && (
                            <span>
                                {t("start-date")}: {oldStartDate}
                            </span>
                        )}
                        {oldEndDate && (
                            <span>
                                {t("end-date")}: {oldEndDate}
                            </span>
                        )}
                        {typeof oldAmount === "number" && (
                            <span>
                                {t("amount")}: {oldAmount}
                            </span>
                        )}
                        {typeof oldHour === "number" && (
                            <span>
                                {firstToUpper(t("hour"))}: {oldHour}
                            </span>
                        )}
                    </div>
                );

                return (
                    <div key={week} className="d-flex align-items-center gap-2">
                        <span>{t("dynamicWeek", { number: weekNumber })}</span>
                        <OldValue value={formattedOldValue} />
                        <NewValue value={formattedNewValue} />
                    </div>
                );
            });
        case "weekDays":
            const newValueWeekDays = (
                <div className="d-flex flex-column">
                    {data[item]?.new.map((item) => {
                        return <div key={item}>{firstToUpper(item)}</div>;
                    })}
                </div>
            );
            const oldValueWeekDays = (
                <div className="d-flex flex-column">
                    {data[item]?.old.map((item) => {
                        return <div key={item}>{firstToUpper(item)}</div>;
                    })}
                </div>
            );

            return (
                <div className="d-flex align-items-center">
                    <OldValue value={oldValueWeekDays} />
                    <NewValue value={newValueWeekDays} />
                </div>
            );
        default:
            return null;
    }
};

const RenderItem = ({ data }) => {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();

    if (!data) return null;

    return (
        <div className="tooltip-content-style text-left">
            {fields?.map((item) => {
                if (!data[item]) return null;

                return (
                    <div key={item} className="py-1 d-flex flex-column gap-2">
                        <Label value={item === "level" ? t("sub-account") : t(item)} />{" "}
                        {budgetCases(item, data, currency, t)}
                    </div>
                );
            })}
        </div>
    );
};

export default RenderItem;
